/**
 * metaLeadGen/mockData.ts
 *
 * Realistic mock data for Jan 1 – Jan 23, 2026.
 * Covers multiple campaigns, ad sets, ads, countries, devices, platforms, genders, ages, and creative types.
 * All rows are atomic — not pre-aggregated.
 *
 * To replace with live data: swap RAW_META_LEAD_GEN_ROWS with adaptMetaApiResponse() from the adapter.
 */

import type { MetaLeadGenRow } from "./types";

// ─────────────────────────────────────────────
// DETERMINISTIC SEEDED GENERATOR
// Produces consistent "random" numbers so mock data is stable across reloads.
// ─────────────────────────────────────────────
function hash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
    h = h >>> 0; // keep 32-bit unsigned
  }
  return h;
}

function seededFloat(seed: string, min: number, max: number): number {
  return min + (hash(seed) % 10000) / 10000 * (max - min);
}

function seededInt(seed: string, min: number, max: number): number {
  return Math.floor(seededFloat(seed, min, max + 1));
}

// ─────────────────────────────────────────────
// CAMPAIGNS
// ─────────────────────────────────────────────
const CAMPAIGNS = [
  { id: "camp_001", name: "UU | Lead Gen | VSL | Cold" },
  { id: "camp_002", name: "UU | Lead Gen | Retarget | Warm" },
] as const;

// ─────────────────────────────────────────────
// AD SETS
// ─────────────────────────────────────────────
const ADSETS = [
  { id: "adset_001", name: "UU | Cold | 18-34 | US-CA | Mobile",      campaignId: "camp_001" },
  { id: "adset_002", name: "UU | Cold | 25-44 | GB-AU | Desktop",     campaignId: "camp_001" },
  { id: "adset_003", name: "UU | Cold | 18-44 | Global | All Devices", campaignId: "camp_001" },
  { id: "adset_004", name: "UU | Warm | Retarget | US | Mobile",       campaignId: "camp_002" },
  { id: "adset_005", name: "UU | Warm | Retarget | Global | All",      campaignId: "camp_002" },
] as const;

// ─────────────────────────────────────────────
// ADS — names include creative type tags for classifyCreativeType()
// ─────────────────────────────────────────────
const ADS = [
  { id: "ad_001", name: "UU | VID_Hook_VSL_v1 | Fear_Angle",         adsetId: "adset_001", campaignId: "camp_001" },
  { id: "ad_002", name: "UU | IMG_Static_Offer_v1 | Direct_CTA",      adsetId: "adset_001", campaignId: "camp_001" },
  { id: "ad_003", name: "UU | UGC_Testimonial_v2 | Social_Proof",     adsetId: "adset_002", campaignId: "camp_001" },
  { id: "ad_004", name: "UU | CAR_Carousel_v1 | Before_After",        adsetId: "adset_002", campaignId: "camp_001" },
  { id: "ad_005", name: "UU | VIDEO_Cold_v3 | Curiosity_Hook",        adsetId: "adset_003", campaignId: "camp_001" },
  { id: "ad_006", name: "UU | STATIC_Cold_v2 | Value_Prop",           adsetId: "adset_003", campaignId: "camp_001" },
  { id: "ad_007", name: "UU | VIDEO_Retarget_v1 | Reminder_Offer",    adsetId: "adset_004", campaignId: "camp_002" },
  { id: "ad_008", name: "UU | UGC_Warm_v1 | Testimonial_Retarget",    adsetId: "adset_004", campaignId: "camp_002" },
  { id: "ad_009", name: "UU | IMG_Retarget_v2 | Discount_CTA",        adsetId: "adset_005", campaignId: "camp_002" },
  { id: "ad_010", name: "UU | VID_Warm_v2 | Last_Chance",             adsetId: "adset_005", campaignId: "camp_002" },
] as const;

// ─────────────────────────────────────────────
// DIMENSION COMBOS — each ad is split across these breakdowns per day
// ─────────────────────────────────────────────
type Gender = "male" | "female" | "unknown";
type Device = "mobile" | "desktop" | "tablet";
type Platform = "facebook" | "instagram" | "messenger" | "audience_network";

interface DimensionCombo {
  age: string;
  gender: Gender;
  device: Device;
  platform: Platform;
  country: string;
  spendWeight: number;   // relative spend multiplier for this segment
  ctrBoost: number;      // CTR multiplier
  cvBoost: number;       // conversion rate multiplier
}

const DIMENSION_COMBOS: DimensionCombo[] = [
  { age: "18-24", gender: "male",    device: "mobile",  platform: "instagram",   country: "US", spendWeight: 1.0, ctrBoost: 1.2, cvBoost: 0.9 },
  { age: "18-24", gender: "female",  device: "mobile",  platform: "instagram",   country: "US", spendWeight: 1.1, ctrBoost: 1.3, cvBoost: 1.0 },
  { age: "25-34", gender: "male",    device: "mobile",  platform: "facebook",    country: "US", spendWeight: 1.3, ctrBoost: 1.1, cvBoost: 1.2 },
  { age: "25-34", gender: "female",  device: "mobile",  platform: "facebook",    country: "US", spendWeight: 1.2, ctrBoost: 1.2, cvBoost: 1.3 },
  { age: "25-34", gender: "male",    device: "desktop", platform: "facebook",    country: "GB", spendWeight: 0.8, ctrBoost: 0.9, cvBoost: 1.1 },
  { age: "35-44", gender: "male",    device: "desktop", platform: "facebook",    country: "CA", spendWeight: 0.9, ctrBoost: 0.8, cvBoost: 1.0 },
  { age: "35-44", gender: "female",  device: "mobile",  platform: "instagram",   country: "CA", spendWeight: 1.0, ctrBoost: 1.0, cvBoost: 1.1 },
  { age: "45-54", gender: "male",    device: "desktop", platform: "facebook",    country: "AU", spendWeight: 0.7, ctrBoost: 0.7, cvBoost: 0.9 },
  { age: "45-54", gender: "female",  device: "tablet",  platform: "facebook",    country: "AU", spendWeight: 0.6, ctrBoost: 0.8, cvBoost: 1.0 },
  { age: "55-64", gender: "male",    device: "desktop", platform: "facebook",    country: "GB", spendWeight: 0.5, ctrBoost: 0.6, cvBoost: 0.8 },
];

// ─────────────────────────────────────────────
// BASE METRICS PER AD TYPE
// Cold = higher spend, lower CTR/CVR; Warm = lower spend, higher CTR/CVR
// ─────────────────────────────────────────────
interface AdBaseMetrics {
  baseSpend: number;       // daily spend for this ad (before combo weight)
  baseImpressions: number; // daily impressions
  baseCtr: number;         // base CTR as decimal (0.008 = 0.8%)
  lpvRate: number;         // what % of clicks become LPVs (0.88 = 88%)
  baseCvr: number;         // base conversion rate LPV→lead (0.04 = 4%)
  bounceRate: number;      // % of LPVs that bounce (0.25 = 25%)
}

const AD_BASE_METRICS: Record<string, AdBaseMetrics> = {
  ad_001: { baseSpend: 48,  baseImpressions: 7200,  baseCtr: 0.0110, lpvRate: 0.89, baseCvr: 0.048, bounceRate: 0.24 },
  ad_002: { baseSpend: 35,  baseImpressions: 5800,  baseCtr: 0.0085, lpvRate: 0.87, baseCvr: 0.038, bounceRate: 0.28 },
  ad_003: { baseSpend: 42,  baseImpressions: 6500,  baseCtr: 0.0095, lpvRate: 0.91, baseCvr: 0.052, bounceRate: 0.21 },
  ad_004: { baseSpend: 30,  baseImpressions: 5200,  baseCtr: 0.0080, lpvRate: 0.86, baseCvr: 0.040, bounceRate: 0.30 },
  ad_005: { baseSpend: 55,  baseImpressions: 8100,  baseCtr: 0.0120, lpvRate: 0.90, baseCvr: 0.055, bounceRate: 0.22 },
  ad_006: { baseSpend: 28,  baseImpressions: 4800,  baseCtr: 0.0075, lpvRate: 0.85, baseCvr: 0.035, bounceRate: 0.32 },
  ad_007: { baseSpend: 25,  baseImpressions: 3800,  baseCtr: 0.0160, lpvRate: 0.93, baseCvr: 0.072, bounceRate: 0.18 },
  ad_008: { baseSpend: 20,  baseImpressions: 3200,  baseCtr: 0.0180, lpvRate: 0.94, baseCvr: 0.080, bounceRate: 0.15 },
  ad_009: { baseSpend: 18,  baseImpressions: 2900,  baseCtr: 0.0150, lpvRate: 0.92, baseCvr: 0.068, bounceRate: 0.20 },
  ad_010: { baseSpend: 22,  baseImpressions: 3500,  baseCtr: 0.0170, lpvRate: 0.93, baseCvr: 0.075, bounceRate: 0.16 },
};

// Day-of-week spend multiplier (0 = Monday in Jan 2026 calendar)
function dayOfWeekMultiplier(date: string): number {
  const dow = new Date(date).getDay(); // 0=Sun, 6=Sat
  const multipliers = [0.80, 1.00, 1.05, 1.08, 1.10, 1.05, 0.85];
  return multipliers[dow] ?? 1.0;
}

// ─────────────────────────────────────────────
// ROW GENERATOR
// ─────────────────────────────────────────────
function generateRows(): MetaLeadGenRow[] {
  const rows: MetaLeadGenRow[] = [];

  // Jan 1 – Jan 23, 2026
  const dates: string[] = [];
  for (let d = 1; d <= 23; d++) {
    dates.push(`2026-01-${String(d).padStart(2, "0")}`);
  }

  for (const date of dates) {
    const dowMult = dayOfWeekMultiplier(date);

    for (const ad of ADS) {
      const base = AD_BASE_METRICS[ad.id];
      if (!base) continue;

      const adset = ADSETS.find(a => a.id === ad.adsetId);
      const campaign = CAMPAIGNS.find(c => c.id === ad.campaignId);
      if (!adset || !campaign) continue;

      // Each ad gets a subset of dimension combos (not all 10 — realistic)
      // Use hash to pick 3-5 combos per ad consistently
      const numCombos = seededInt(`${ad.id}_num`, 3, 5);
      const startCombo = seededInt(`${ad.id}_start`, 0, DIMENSION_COMBOS.length - 1);

      for (let ci = 0; ci < numCombos; ci++) {
        const combo = DIMENSION_COMBOS[(startCombo + ci) % DIMENSION_COMBOS.length];
        const seed = `${date}_${ad.id}_${ci}`;

        // Apply multipliers + small daily noise
        const noise = seededFloat(`${seed}_noise`, 0.88, 1.14);
        const totalMult = dowMult * combo.spendWeight * noise;

        const spend = Math.round(base.baseSpend * totalMult * 100) / 100;
        const impressions = Math.round(base.baseImpressions * totalMult);
        const clicks = Math.round(impressions * base.baseCtr * combo.ctrBoost * seededFloat(`${seed}_ctr`, 0.92, 1.08));
        const landingPageViews = Math.round(clicks * base.lpvRate * seededFloat(`${seed}_lpv`, 0.95, 1.05));
        const leads = Math.round(landingPageViews * base.baseCvr * combo.cvBoost * seededFloat(`${seed}_cvr`, 0.90, 1.10));
        const bounces = Math.round(landingPageViews * base.bounceRate * seededFloat(`${seed}_bounce`, 0.88, 1.12));

        const reach = Math.round(impressions * seededFloat(`${seed}_reach`, 0.55, 0.75));

        rows.push({
          date,
          campaignId: campaign.id,
          campaignName: campaign.name,
          adsetId: adset.id,
          adsetName: adset.name,
          adId: ad.id,
          adName: ad.name,
          age: combo.age,
          gender: combo.gender,
          device: combo.device,
          platform: combo.platform,
          country: combo.country,
          spend,
          impressions,
          clicks,
          landingPageViews: Math.max(0, landingPageViews),
          leads: Math.max(0, leads),
          bounces: Math.max(0, bounces),
          reach: Math.max(1, reach),
        });
      }
    }
  }

  return rows;
}

export const RAW_META_LEAD_GEN_ROWS: MetaLeadGenRow[] = generateRows();
